// SentForSignaturePanel — what happened after "Send for signature"
// (E-signature Phase 1, second half).
//
// CONSUMER: internal-ui.
//
// The send flow (PrepareAndSend) shows a one-time confirmation
// and then the trail goes cold — there was nowhere to check "did Sam
// sign it yet?" without asking the client directly, no way to recover
// from a failed delivery email, and no way to call back a document
// sent to the wrong person. This panel closes that loop: every envelope
// sent for this record, each signer's status, and the actions that only
// make sense while an envelope is still pending — Resend (a fresh link
// to the same address), "Send elsewhere" (the same request to a
// DIFFERENT address, when the one on file is wrong or unreachable),
// "Copy a link" (a working link handed straight to the advisor, no
// email attempted at all — Grant, 2026-09-19: "send elsewhere means
// getting a link to send"), and Cancel (call the whole thing back).
//
// Per-signer status arrived 2026-09-19 with the address change, from the
// same piece of feedback. The panel could say a document was "Partly
// signed" and then list its signers with nothing to tell them apart —
// Nate, looking at exactly that: "i also don't see who". It also offered
// Resend beside a person who had already signed, which would have sent a
// client a fresh request for something they had finished.
//
// Resend and Send elsewhere now hand the link over EVEN WHEN the email
// succeeded, not only when it fails. The link was always minted; only
// showing it on failure meant "text it too" had no path but breaking
// the email first.
//
// Renders nothing when nothing has ever been sent — this is a status
// panel for a workflow the record has actually used, not permanent
// chrome on every Processing record.

import { useCallback, useEffect, useState } from "react";

import { Link } from "react-router-dom";
import { Pencil } from "lucide-react";

import {
  apiV11,
  ApiError,
  type EsignDraft,
  type EsignDraftDocument,
  type EsignEnvelopeRec,
  type EsignEnvelopeSignerRec,
} from "../../api";
import { Button, ErrorBanner } from "../../ui";
import { ChangeSignerEmailDialog, type ChangeSignerEmailTarget } from "./ChangeSignerEmailDialog";
import { copyTextToClipboard } from "./copy-to-clipboard";
import { Modal } from "../Modal";
import { StatusPill } from "../StatusPill";
import { esignEnvelopeStatus, esignSignerStatus } from "../status-pill-helpers";
import { useToast } from "../Toast";
import { VoidNoticePreview } from "./VoidNoticePreview";
import type { EsignSendableKind } from "../../../../shared/esign/sendable-kinds";
import { toFriendlyInvitation } from "@shared/esign/signing-invitation";

// Matches CANCELLABLE_STATUSES in shared/db/esign-send.ts — the server
// is the actual gate; this only decides whether the buttons show up.
const PENDING_STATUSES = new Set(["sent", "inprogress", "in progress"]);

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** A signer nobody can be asked about any more. Their answer is on the
 *  document, so neither a resend nor an address change applies — and a
 *  signature belongs to the address that made it, which is why the
 *  server refuses to move one even when the rest of the document is
 *  still open. */
function hasFinished(signer: EsignEnvelopeSignerRec): boolean {
  return Boolean(signer.signedAt) || signer.status === "signed" || signer.status === "declined";
}

export interface SentForSignaturePanelProps {
  /** Which kind of record this is. Generalised 2026-08-24: the panel
   *  was pinned to Processing records, so the four other places a
   *  document can now be sent FROM (household, person, opportunity,
   *  service record) had no way to see what was outstanding without
   *  leaving the record for the E-Sign page. The API behind it already
   *  served every kind. */
  recordKind: EsignSendableKind;
  recordId: string;
  /** "panel" (default) is the original list of every envelope ever sent,
   *  used on households, people and service records. "inflight" is the
   *  Documents tab on Opportunity and Processing records (Nate Swartz,
   *  2026-09-29, docs/design/documents-tab-workflow.md): only packages
   *  that still need a hand are shown, because a completed package "is
   *  just a signed document": its signed copies are filed on the tab
   *  (on "Visible to client" when a client signed, since PR 9a; Internal
   *  otherwise), each naming the package. Voided (cancelled/revoked) packages are left off too;
   *  their record is the Trail and the envelope page. */
  variant?: "panel" | "inflight";
  /** Hands the loaded envelopes to the Documents tab, which uses them to
   *  give a signed file its "Signers & history" link without a second
   *  request per row. Pass a stable function (a state setter): it is a
   *  dependency of the loader, so a new function every render would
   *  reload in a loop. */
  onEnvelopesLoaded?: (envelopes: EsignEnvelopeRec[]) => void;
  /** "Resend" on an expired package (inflight only), when onOpenDraft is
   *  absent: opens the signing screen on the package's document, with
   *  signers and fields chosen anew. Absent = no button. */
  onResendPackage?: ((envelope: EsignEnvelopeRec) => void) | null;
  /** The send screen on a draft the server copied from a package
   *  (Documents tab PR 10a): "Correct & resend" after the void, and
   *  "Resend" on an expired or declined package. When given, Resend uses
   *  it, and every package out for signature gets "Correct & resend".
   *  `fieldsKept` false: the package predates drafts, and its documents
   *  will be read and their fields suggested again. */
  onOpenDraft?: ((draft: EsignDraft, info: { fieldsKept: boolean; droppedDocuments: string[] }) => void) | null;
  /** "Fix what hasn't been signed yet" (PR 10b): the send screen in
   *  correction mode, on the package's own saved work. Absent = that
   *  choice is shown but can't be picked. */
  onOpenCorrection?:
    | ((
        envelope: EsignEnvelopeRec,
        copy: { documents: EsignDraftDocument[]; state: unknown; realtyOfferId: string | null },
        reason: string,
      ) => void)
    | null;
}

/** In flight: still out, or stopped in a way someone has to act on. */
// "sendfailed" is the signing service refusing the package: it must surface
// as a failure someone acts on, never sit out of sight (or read "Sent").
const IN_FLIGHT_STATUSES = new Set(["sent", "inprogress", "in progress", "declined", "expired", "sendfailed"]);
const NEEDS_A_HAND_STATUSES = new Set(["declined", "expired", "sendfailed"]);

/** One word-state per signer, so the row never leans on an icon to say
 *  where the person has got to (Grant, 2026-09-29: "just not sure what
 *  the icons mean"). */
export function signerStateWords(
  signer: EsignEnvelopeSignerRec,
  envelopePending: boolean,
): { text: string; tone: "done" | "waiting" | "stopped" | "quiet" } {
  if (signer.signedAt || signer.status === "signed") {
    const d = formatShortDate(signer.signedAt);
    return { text: d ? `Signed ${d}` : "Signed", tone: "done" };
  }
  if (signer.declinedAt || signer.status === "declined") {
    const d = formatShortDate(signer.declinedAt);
    return { text: d ? `Declined ${d}` : "Declined", tone: "stopped" };
  }
  if (!envelopePending) return { text: "Link closed", tone: "quiet" };
  // Outlook refused the signing email (2026-09-29). "Didn't go out", never
  // "bounced": the OS cannot see mail returned by the client's server.
  if (signer.emailFailedAt) {
    const d = formatShortDate(signer.emailFailedAt);
    return { text: d ? `Email didn't go out ${d}` : "Email didn't go out", tone: "stopped" };
  }
  if (signer.status === "viewed") return { text: "Viewed · not signed", tone: "waiting" };
  if (signer.status === "delivered" || signer.status === "pending")
    return { text: "Sent · not opened yet", tone: "waiting" };
  return { text: "Not sent yet", tone: "quiet" };
}

function formatShortDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function SentForSignaturePanel({
  recordKind,
  recordId,
  variant = "panel",
  onEnvelopesLoaded,
  onResendPackage = null,
  onOpenDraft = null,
  onOpenCorrection = null,
}: SentForSignaturePanelProps) {
  const toast = useToast();
  const [envelopes, setEnvelopes] = useState<EsignEnvelopeRec[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<EsignEnvelopeRec | null>(null);
  // The void reason (2026-09-29): required on the Documents tab, and the
  // signers see it in the "nothing to sign" email.
  const [voidReason, setVoidReason] = useState("");
  // Whether the void emails the signers (Baker, 2026-10-05). Ticked by
  // default, which is what every void did before there was a choice; reset
  // whenever a void dialog closes so one unticked void never carries over.
  const [notifySigners, setNotifySigners] = useState(true);
  // "Correct & resend" (PR 10a): which package, and why. The only choice
  // that can be taken today is "Change the document itself" (void, then
  // the same package as a new draft); "Fix what hasn't been signed yet"
  // is drawn and explained, and arrives with PR 10b.
  const [correctTarget, setCorrectTarget] = useState<EsignEnvelopeRec | null>(null);
  const [correctReason, setCorrectReason] = useState("");
  // Which case (PR 10b). "fix" keeps the signatures already given; it is
  // the default whenever it can be picked.
  const [correctMode, setCorrectMode] = useState<"fix" | "change">("fix");
  const [fixNote, setFixNote] = useState<string | null>(null);
  const fixAvailable = onOpenCorrection !== null;
  const openCorrect = (envelope: EsignEnvelopeRec) => {
    setCorrectTarget(envelope);
    setCorrectMode(fixAvailable ? "fix" : "change");
    setFixNote(null);
  };
  const closeCorrect = () => {
    setCorrectTarget(null);
    setCorrectReason("");
    setFixNote(null);
    setNotifySigners(true);
  };
  // The fresh signing link, held for hand-delivery — originally only
  // for when a resend email could not go (2026-08-25: "copy it
  // yourself" with nothing to copy is a dead end), now also the
  // deliverable for "Copy a link" (2026-09-19), which never even
  // attempts an email. `hint` carries which of those it is, since the
  // three actions that can set this explain themselves differently.
  const [fallbackLink, setFallbackLink] = useState<{
    name: string;
    url: string;
    hint: string;
  } | null>(null);
  const [changeTarget, setChangeTarget] = useState<ChangeSignerEmailTarget | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiV11.listEsignEnvelopes(recordKind, recordId);
      setEnvelopes(res.envelopes);
      onEnvelopesLoaded?.(res.envelopes);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : "Couldn't load e-signature status.");
    }
  }, [recordKind, recordId, onEnvelopesLoaded]);

  useEffect(() => {
    void load();
  }, [load]);

  // Keep a PENDING envelope's status live. The completion webhook lands
  // within about a minute, but nothing told the screen — an advisor sat
  // on "Waiting on signature" indefinitely and only a manual reload
  // flipped it to "Signed" (staging round 2, 2026-08-23).
  //
  // Deliberately narrow: polls ONLY while something is actually pending
  // (a settled panel makes no requests at all), pauses while the tab is
  // hidden, and re-checks immediately on refocus — which covers the
  // common case of an advisor switching to email and back.
  const hasPending = (envelopes ?? []).some((e) => PENDING_STATUSES.has(e.status.toLowerCase()));
  useEffect(() => {
    if (!hasPending) return;
    const POLL_MS = 20_000;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, POLL_MS);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [hasPending, load]);

  // A correction waiting on the signing service (PR 10b): ask it every
  // 20 seconds while the tab is visible, and reload once it has applied
  // or failed. The webhook usually gets there first; this is what makes
  // the row honest when it doesn't.
  const pendingCorrectionIds = (envelopes ?? [])
    .filter((e) => e.correction?.status === "pending")
    .map((e) => e.id)
    .join(",");
  useEffect(() => {
    if (!pendingCorrectionIds) return;
    const ids = pendingCorrectionIds.split(",");
    const tick = async () => {
      if (document.visibilityState !== "visible") return;
      let changed = false;
      for (const id of ids) {
        try {
          const res = await apiV11.checkEsignCorrection(id);
          if (res.status !== "pending") changed = true;
        } catch {
          // A failed check is a check that didn't happen; the next one runs.
        }
      }
      if (changed) void load();
    };
    const timer = setInterval(() => void tick(), 20_000);
    void tick();
    return () => clearInterval(timer);
  }, [pendingCorrectionIds, load]);

  async function handleResend(envelopeId: string, signerId: string, signerName: string) {
    const key = `resend:${envelopeId}:${signerId}`;
    setBusyKey(key);
    setFallbackLink(null);
    try {
      const res = await apiV11.resendEsignSigner(envelopeId, signerId);
      if (res.emailed) {
        // The email went — but the link the server just minted is
        // right here too. Discarding it because the primary channel
        // worked is why "just give me the link" had no answer before
        // 2026-09-19: showing it costs nothing and saves a second trip
        // for whoever wants to text it as well.
        toast.success("Sent a new link.");
        setFallbackLink({
          name: signerName,
          url: res.sign_url,
          hint: `A fresh link went to ${signerName}. It's below too, in case you'd rather text it.`,
        });
      } else {
        toast.success(
          "New link ready — the email couldn't go out, so copy it below and send it yourself.",
        );
        setFallbackLink({
          name: signerName,
          url: res.sign_url,
          hint: `A fresh link was created for ${signerName}, but the email didn't go out. ${res.email_error ?? ""} Copy it and send it however you like — text, or your own email.`,
        });
      }
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't resend that link.");
    } finally {
      setBusyKey(null);
    }
  }

  // "Give me the link and I'll text it" (Grant, 2026-09-19) — no email
  // is attempted at all, and unlike Resend this never touches the link
  // the signer already has. Reaches for the same fallback display
  // Resend uses; a second, near-identical UI for "here is a link" would
  // be the wrong kind of distinct.
  async function handleCopyLink(envelopeId: string, signerId: string, signerName: string) {
    const key = `copy-link:${envelopeId}:${signerId}`;
    setBusyKey(key);
    setFallbackLink(null);
    try {
      const res = await apiV11.copyEsignSignerLink(envelopeId, signerId);
      toast.success(`Link ready for ${signerName}.`);
      setFallbackLink({
        name: signerName,
        url: res.sign_url,
        hint: `A link for ${signerName} — nothing was emailed. Copy it and send it however you like.`,
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't get a link for that signer.");
    } finally {
      setBusyKey(null);
    }
  }

  async function handleCancel(envelope: EsignEnvelopeRec) {
    setBusyKey(`cancel:${envelope.id}`);
    try {
      if (variant === "inflight") {
        const res = await apiV11.cancelEsignEnvelope(envelope.id, voidReason.trim(), {
          notifySigners,
        });
        const notices = res.notices ?? [];
        const missed = notices.filter((n) => !n.emailed).map((n) => n.name);
        if (missed.length > 0) {
          toast.error(
            `Voided "${envelope.title ?? "the package"}", but the email to ${missed.join(" and ")} didn't go out. Let them know yourself.`,
          );
        } else {
          toast.success(
            !notifySigners
              ? `Voided "${envelope.title ?? "the package"}". Nobody was emailed.`
              : notices.length > 0
              ? `Voided "${envelope.title ?? "the package"}". ${notices.map((n) => n.name).join(" and ")} ${notices.length === 1 ? "was" : "were"} told.`
              : `Voided "${envelope.title ?? "the package"}".`,
          );
        }
      } else {
        await apiV11.cancelEsignEnvelope(envelope.id);
        toast.success(`Cancelled "${envelope.title ?? "the document"}".`);
      }
      setCancelTarget(null);
      setVoidReason("");
      setNotifySigners(true);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't cancel this request.");
    } finally {
      setBusyKey(null);
    }
  }

  function openCopy(draft: EsignDraft, fieldsKept: boolean, droppedDocuments: string[]) {
    onOpenDraft?.(draft, { fieldsKept, droppedDocuments });
  }

  // "Fix what hasn't been signed yet": open the send screen on the
  // package's own saved work, in correction mode. Nothing is changed or
  // sent until "Send the correction" there.
  async function handleOpenFix(envelope: EsignEnvelopeRec) {
    setBusyKey(`correct:${envelope.id}`);
    try {
      const res = await apiV11.openEsignEnvelopeForCorrection(envelope.id);
      if (!res.fields_kept || res.dropped_documents.length > 0) {
        setFixNote(
          res.fields_kept
            ? `${res.dropped_documents.join(", ")} ${res.dropped_documents.length === 1 ? "isn't" : "aren't"} on this record any more, so this package can't be corrected in place. Use "Change the document itself".`
            : 'This package was sent before the OS kept its boxes, so it can\'t be corrected in place. Use "Change the document itself".',
        );
        setCorrectMode("change");
        return;
      }
      const reason = correctReason.trim();
      closeCorrect();
      onOpenCorrection?.(envelope, res.copy, reason);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't open this package to correct it.");
    } finally {
      setBusyKey(null);
    }
  }

  // Void, then reopen as a new package. The copy is saved FIRST: a
  // package is never withdrawn before its replacement exists. If the void
  // then fails, the copy is discarded so the tab doesn't grow a stray
  // draft beside a package that is still out.
  async function handleCorrect(envelope: EsignEnvelopeRec) {
    setBusyKey(`correct:${envelope.id}`);
    let copy: Awaited<ReturnType<typeof apiV11.redraftEsignEnvelope>> | null = null;
    try {
      copy = await apiV11.redraftEsignEnvelope(envelope.id, "correct");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't copy this package. Nothing was voided.");
      setBusyKey(null);
      return;
    }
    try {
      const res = await apiV11.cancelEsignEnvelope(envelope.id, correctReason.trim(), {
        notifySigners,
      });
      const notices = res.notices ?? [];
      const missed = notices.filter((n) => !n.emailed).map((n) => n.name);
      if (missed.length > 0) {
        toast.error(
          `Voided "${envelope.title ?? "the package"}", but the email to ${missed.join(" and ")} didn't go out. Let them know yourself.`,
        );
      } else {
        toast.success(
          !notifySigners
            ? `Voided "${envelope.title ?? "the package"}". Nobody was emailed. Make your change and send it again.`
            : notices.length > 0
            ? `Voided "${envelope.title ?? "the package"}". ${notices.map((n) => n.name).join(" and ")} ${notices.length === 1 ? "was" : "were"} told. Make your change and send it again.`
            : `Voided "${envelope.title ?? "the package"}". Make your change and send it again.`,
        );
      }
      closeCorrect();
      await load();
      openCopy(copy.draft, copy.fields_kept, copy.dropped_documents);
    } catch (err) {
      await apiV11.discardEsignDraft(recordKind, recordId, copy.draft.id).catch(() => undefined);
      toast.error(
        err instanceof ApiError
          ? `${err.message} The package is still out for signature.`
          : "Couldn't void this package. It's still out for signature.",
      );
    } finally {
      setBusyKey(null);
    }
  }

  async function handleResendCopy(envelope: EsignEnvelopeRec) {
    setBusyKey(`resend-copy:${envelope.id}`);
    try {
      const copy = await apiV11.redraftEsignEnvelope(envelope.id, "resend");
      openCopy(copy.draft, copy.fields_kept, copy.dropped_documents);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't open this package again.");
    } finally {
      setBusyKey(null);
    }
  }

  if (loadError) return <ErrorBanner message={loadError} />;
  // Loading, or nothing has ever been sent from this record — either
  // way, there is nothing worth showing yet.
  if (!envelopes) return null;
  if (envelopes.length === 0) return variant === "inflight" ? <InFlightEmpty /> : null;

  if (variant === "inflight") {
    const inFlight = envelopes.filter((e) => IN_FLIGHT_STATUSES.has(e.status.toLowerCase()));
    const canResend = Boolean(onOpenDraft || onResendPackage);
    const drafts = envelopes.filter((e) => e.status.toLowerCase() === "draft");
    // A package needs a hand when it stopped (declined, expired) or when a
    // signing email never left Outlook: nobody is going to sign that one.
    const emailFailed = (e: EsignEnvelopeRec) =>
      PENDING_STATUSES.has(e.status.toLowerCase()) &&
      e.signers.some((s) => s.emailFailedAt && !hasFinished(s));
    const wantsHand = (e: EsignEnvelopeRec) =>
      NEEDS_A_HAND_STATUSES.has(e.status.toLowerCase()) || emailFailed(e);
    const needsHand = inFlight.some(wantsHand);
    // Those first: they are what someone must act on.
    const ordered = [...inFlight].sort((a, b) => Number(wantsHand(b)) - Number(wantsHand(a)));
    return (
      <>
        <section className="doc-card-packages" aria-label="E-sign packages in flight">
          <h3 className="doc-card-sec">
            {needsHand ? "Needs a hand" : "Out for signature"} · {inFlight.length}
            <small>Only packages still in flight show here</small>
          </h3>
          {fallbackLink ? (
            <div className="esign-envelope-fallback-link">
              <span className="esign-prep-hint">{fallbackLink.hint}</span>
              <code>{fallbackLink.url}</code>
              <Button
                variant="sw-default"
                size="sm"
                onClick={() => {
                  void copyTextToClipboard(fallbackLink.url).then((ok) => {
                    if (ok)
                      toast.success(`Link copied — send it to ${fallbackLink.name} yourself.`);
                    else toast.error("Couldn't copy — select the link text and copy it.");
                  });
                }}
              >
                Copy link
              </Button>
            </div>
          ) : null}
          {ordered.length === 0 ? (
            <p className="doc-card-empty">Nothing out for signature.</p>
          ) : (
            ordered.map((envelope) => {
              const status = envelope.status.toLowerCase();
              const pending = PENDING_STATUSES.has(status);
              const stopped = NEEDS_A_HAND_STATUSES.has(status) || emailFailed(envelope);
              const signedCount = envelope.signers.filter(
                (s) => s.signedAt || s.status === "signed",
              ).length;
              const sentDate = formatShortDate(envelope.sentAt);
              const decliner = envelope.signers.find(
                (s) => s.declinedAt || s.status === "declined",
              );
              return (
                <article
                  key={envelope.id}
                  className={`doc-pkg${stopped ? " doc-pkg--stopped" : " doc-pkg--out"}`}
                >
                  <header className="doc-pkg-row">
                    <span className="doc-pkg-main">
                      <span className="doc-pkg-name">{envelope.title ?? "Untitled package"}</span>
                      <span className="doc-pkg-meta">
                        {status === "sendfailed"
                          ? "Didn't send"
                          : sentDate
                            ? `Sent ${sentDate}`
                            : "Not sent yet"}
                      </span>
                    </span>
                    <span className="doc-pkg-count">
                      {signedCount} of {envelope.signers.length} signed
                    </span>
                    <StatusPill {...esignEnvelopeStatus(envelope.status)} />
                    {/* Declined and expired are already closed at the provider
                        (the server refuses to void them), so the way forward is
                        a new package on the same document. */}
                    {(status === "expired" || status === "declined" || status === "sendfailed") && canResend ? (
                      <button
                        type="button"
                        className="doc-btn doc-btn--sm"
                        disabled={busyKey === `resend-copy:${envelope.id}`}
                        onClick={() =>
                          onOpenDraft ? void handleResendCopy(envelope) : onResendPackage?.(envelope)
                        }
                      >
                        Resend
                      </button>
                    ) : null}
                  </header>
                  <div className="doc-pkg-body">
                    {status === "declined" && decliner ? (
                      <p className="doc-pkg-note">
                        <strong>
                          {decliner.name} declined
                          {decliner.declinedAt ? ` on ${formatShortDate(decliner.declinedAt)}` : ""}
                          {decliner.declineReason ? ":" : "."}
                        </strong>{" "}
                        {decliner.declineReason ? (
                          <>&ldquo;{decliner.declineReason}&rdquo; </>
                        ) : (
                          "They didn't give a reason. "
                        )}
                        The other signers&apos; links are closed.{" "}
                        {onOpenDraft
                          ? "Resend opens the same package with everything kept, ready to change and send."
                          : onResendPackage
                            ? "Resend opens a corrected package on the same document."
                            : "Send a corrected package from the document on this record."}
                      </p>
                    ) : null}
                    {pending
                      ? envelope.signers
                          .filter((sg) => sg.emailFailedAt && !hasFinished(sg))
                          .map((sg) => (
                            <p key={`fail-${sg.signerId}`} className="doc-pkg-note">
                              <strong>
                                The email to {sg.emailFailedAddress ?? sg.email} didn&apos;t go
                                out.
                              </strong>{" "}
                              {sg.emailFailureReason ? `${sg.emailFailureReason} ` : ""}
                              Fix the address and the OS sends a fresh link.
                            </p>
                          ))
                      : null}
                    {status === "sendfailed" ? (
                      <p className="doc-pkg-note" data-testid="esign-send-failed">
                        <strong>This package didn&apos;t send.</strong>{" "}
                        {envelope.voidedReason ? `${envelope.voidedReason} ` : ""}
                        Nobody was asked to sign and no signing links are open.{" "}
                        {onOpenDraft
                          ? "Resend opens the same package with everything kept, ready to fix and send."
                          : onResendPackage
                            ? "Resend opens a corrected package on the same document."
                            : "Send it again from the document on this record."}
                      </p>
                    ) : null}
                    {status === "expired" ? (
                      <p className="doc-pkg-note">
                        <strong>
                          The deadline passed
                          {envelope.expiresAt ? ` on ${formatShortDate(envelope.expiresAt)}` : ""}{" "}
                          before everyone signed.
                        </strong>{" "}
                        {onOpenDraft
                          ? "Resend opens the same package with everything kept."
                          : onResendPackage
                            ? "Resend opens it again on the same document."
                            : "Send it again from the document on this record."}
                      </p>
                    ) : null}
                    {envelope.correction?.status === "pending" ? (
                      <p className="doc-pkg-note doc-pkg-note--info" data-testid="esign-correction-pending">
                        <strong>Correction in progress.</strong> Waiting for the signing service to confirm
                        &ldquo;{envelope.correction.reason.replace(/[.!?]+$/, "")}&rdquo;. The people whose part changed get a fresh link
                        as soon as it does.
                      </p>
                    ) : envelope.correction?.status === "failed" && pending ? (
                      <p className="doc-pkg-note">
                        <strong>The correction didn&apos;t go through.</strong>{" "}
                        {envelope.correction.failureMessage ? `${envelope.correction.failureMessage} ` : ""}
                        Nothing changed: the package is as it was. Try again, or use Correct &amp; resend &rarr;
                        &ldquo;Change the document itself&rdquo;.
                      </p>
                    ) : null}
                    <ul className="doc-signers">
                      {envelope.signers.map((signer) => {
                        const canAsk = pending && !hasFinished(signer);
                        const state = signerStateWords(signer, pending);
                        return (
                          <li key={signer.signerId} className="doc-signer">
                            <span className="doc-signer-name">{signer.name}</span>
                            <span className="doc-signer-email">
                              <span className="doc-signer-email-text">{signer.email}</span>
                              {canAsk ? (
                                <button
                                  type="button"
                                  className="doc-icon-btn"
                                  aria-label={`Send ${signer.name}'s request to a different email`}
                                  title="Send to a different email"
                                  onClick={() =>
                                    setChangeTarget({
                                      envelopeId: envelope.id,
                                      signerId: signer.signerId,
                                      name: signer.name,
                                      email: signer.email,
                                    })
                                  }
                                >
                                  <Pencil size={13} strokeWidth={1.5} aria-hidden />
                                </button>
                              ) : null}
                            </span>
                            <span className={`doc-signer-state doc-signer-state--${state.tone}`}>
                              {state.text}
                              {signer.previousEmail ? (
                                <small>Was {signer.previousEmail}</small>
                              ) : null}
                            </span>
                            <span className="doc-signer-actions">
                              {canAsk && signer.emailFailedAt ? (
                                <button
                                  type="button"
                                  className="doc-btn doc-btn--sm"
                                  onClick={() =>
                                    setChangeTarget({
                                      envelopeId: envelope.id,
                                      signerId: signer.signerId,
                                      name: signer.name,
                                      email: signer.email,
                                    })
                                  }
                                >
                                  Fix email &amp; resend
                                </button>
                              ) : null}
                              {canAsk ? (
                                <>
                                  <button
                                    type="button"
                                    className="doc-btn doc-btn--sm"
                                    disabled={
                                      busyKey === `resend:${envelope.id}:${signer.signerId}`
                                    }
                                    onClick={() =>
                                      handleResend(envelope.id, signer.signerId, signer.name)
                                    }
                                  >
                                    Send reminder
                                  </button>
                                  <button
                                    type="button"
                                    className="doc-link-btn"
                                    disabled={
                                      busyKey === `copy-link:${envelope.id}:${signer.signerId}`
                                    }
                                    onClick={() =>
                                      handleCopyLink(envelope.id, signer.signerId, signer.name)
                                    }
                                  >
                                    Copy a link
                                  </button>
                                </>
                              ) : null}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    {/* What was sent (PR 8b): the subject, note and video
                        the signers were emailed, so "what did we tell
                        them?" is answered here, not in someone's Sent
                        folder. Only for sends made since this existed. */}
                    {envelope.emailSubject || envelope.message || envelope.videoUrl || envelope.emailBody ? (
                      <details className="doc-pkg-sent">
                        <summary>What was sent</summary>
                        <dl>
                          {envelope.emailSubject ? (
                            <>
                              <dt>Subject</dt>
                              <dd>{envelope.emailSubject}</dd>
                            </>
                          ) : null}
                          {envelope.message ? (
                            <>
                              <dt>Note</dt>
                              <dd className="doc-pkg-sent-note">{envelope.message}</dd>
                            </>
                          ) : null}
                          {envelope.emailBody ? (
                            <>
                              <dt>Wording</dt>
                              <dd className="doc-pkg-sent-note">
                                <span className="doc-pkg-sent-tag">Changed for this send</span>
                                {"\n"}
                                {toFriendlyInvitation(envelope.emailBody)}
                              </dd>
                            </>
                          ) : null}
                          {envelope.videoUrl ? (
                            <>
                              <dt>Video</dt>
                              <dd>
                                <a href={envelope.videoUrl} target="_blank" rel="noreferrer">
                                  {envelope.videoTitle ?? "Watch on Loom"}
                                </a>
                              </dd>
                            </>
                          ) : null}
                        </dl>
                      </details>
                    ) : null}
                    <div className="doc-pkg-links">
                      <Link to={`/esign/${envelope.id}`} className="doc-link-btn">
                        Audit trail
                      </Link>
                      {pending && onOpenDraft && envelope.correction?.status !== "pending" ? (
                        <button
                          type="button"
                          className="doc-link-btn"
                          onClick={() => openCorrect(envelope)}
                        >
                          Correct &amp; resend
                        </button>
                      ) : null}
                      {pending ? (
                        <button
                          type="button"
                          className="doc-link-btn doc-link-btn--danger"
                          onClick={() => setCancelTarget(envelope)}
                        >
                          Void
                        </button>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })
          )}
          {drafts.length > 0 ? (
            <p className="doc-card-draftline">
              <strong>
                {drafts.length} draft{drafts.length === 1 ? "" : "s"}
              </strong>{" "}
              · {drafts.map((d) => d.title ?? "Untitled").join(", ")}
            </p>
          ) : null}
        </section>
        <ChangeSignerEmailDialog
          target={changeTarget}
          onClose={() => setChangeTarget(null)}
          onChanged={load}
          onFallbackLink={(link) =>
            setFallbackLink({
              ...link,
              hint: `A fresh link for ${link.name} — the old address's link is dead. Copy it and send it however you like.`,
            })
          }
        />
        <Modal
          open={cancelTarget !== null}
          onClose={() => {
            setCancelTarget(null);
            setVoidReason("");
            setNotifySigners(true);
          }}
          title="Void this package?"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setCancelTarget(null);
                  setVoidReason("");
                  setNotifySigners(true);
                }}
              >
                Keep it
              </Button>
              <Button
                variant="sw-danger"
                disabled={busyKey === `cancel:${cancelTarget?.id}` || voidReason.trim() === ""}
                title={voidReason.trim() === "" ? "Say why first" : undefined}
                onClick={() => cancelTarget && handleCancel(cancelTarget)}
              >
                Void package
              </Button>
            </>
          }
        >
          <p className="doc-void-lede">
            <strong>{cancelTarget?.title ?? "This package"}.</strong> Every open signing link stops
            working.{" "}
            {voidNoticeNames(cancelTarget)
              ? `${voidNoticeNames(cancelTarget)} can get an email saying it was withdrawn, with your reason. You see it below first. `
              : ""}
            Nothing is deleted: the package and its history stay on the envelope page.
          </p>
          <label className="doc-void-field">
            <span className="doc-void-label">
              Reason <small>required · the signers see it</small>
            </span>
            <textarea
              className="doc-void-input"
              rows={3}
              maxLength={500}
              placeholder="Why is this being withdrawn?"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
            />
          </label>
          {cancelTarget ? (
            <VoidNoticePreview
              envelopeId={cancelTarget.id}
              reason={voidReason}
              notify={notifySigners}
              onNotifyChange={setNotifySigners}
            />
          ) : null}
        </Modal>
        <Modal
          open={correctTarget !== null}
          onClose={closeCorrect}
          title="Correct & resend"
          className="doc-correct-modal"
          data-testid="esign-correct"
          footer={
            <>
              <Button variant="ghost" onClick={closeCorrect}>
                Cancel
              </Button>
              <Button
                variant="sw-primary"
                disabled={busyKey === `correct:${correctTarget?.id}` || correctReason.trim() === ""}
                title={correctReason.trim() === "" ? "Say why first" : undefined}
                onClick={() =>
                  correctTarget &&
                  (correctMode === "fix" ? handleOpenFix(correctTarget) : handleCorrect(correctTarget))
                }
              >
                {correctMode === "fix" ? "Open the fields" : "Void and open the new package"}
              </Button>
            </>
          }
        >
          {correctTarget ? (
            <CorrectChoices
              envelope={correctTarget}
              mode={correctMode}
              onMode={setCorrectMode}
              fixAvailable={fixAvailable && fixNote === null}
            />
          ) : null}
          {fixNote ? <p className="doc-correct-note">{fixNote}</p> : null}
          <label className="doc-void-field">
            <span className="doc-void-label">
              Tell the signers why <small>required · goes in their email and the audit trail</small>
            </span>
            <textarea
              className="doc-void-input"
              rows={3}
              maxLength={500}
              placeholder="What changed, in a sentence"
              value={correctReason}
              onChange={(e) => setCorrectReason(e.target.value)}
            />
          </label>
          {/* Only "Change the document itself" voids; "Fix what hasn't been
              signed yet" voids nothing, so there is no email to preview. */}
          {correctTarget && correctMode === "change" ? (
            <VoidNoticePreview
              envelopeId={correctTarget.id}
              reason={correctReason}
              notify={notifySigners}
              onNotifyChange={setNotifySigners}
            />
          ) : null}
        </Modal>
      </>
    );
  }

  return (
    <>
      <div className="esign-status-panel">
        <h3 className="esign-status-panel-title">Sent for signature</h3>
        {fallbackLink ? (
          <>
            <p className="esign-prep-hint">{fallbackLink.hint}</p>
            <div className="esign-envelope-fallback-link">
              <code>{fallbackLink.url}</code>
              <Button
                variant="sw-default"
                size="sm"
                onClick={() => {
                  void copyTextToClipboard(fallbackLink.url).then((ok) => {
                    if (ok)
                      toast.success(`Link copied — send it to ${fallbackLink.name} yourself.`);
                    else toast.error("Couldn't copy — select the link text and copy it.");
                  });
                }}
              >
                Copy link
              </Button>
            </div>
          </>
        ) : null}
        <ul className="esign-status-list">
          {envelopes.map((envelope) => {
            const pending = PENDING_STATUSES.has(envelope.status.toLowerCase());
            const sentDate = formatDate(envelope.sentAt);
            return (
              <li key={envelope.id} className="esign-status-row">
                <div className="esign-status-row-head">
                  <span className="esign-status-row-title">
                    {envelope.title ?? "Untitled document"}
                  </span>
                  <StatusPill {...esignEnvelopeStatus(envelope.status)} />
                  {envelope.status.toLowerCase() === "sendfailed" ? (
                    <span className="esign-status-sent-date">Didn&apos;t send</span>
                  ) : sentDate ? (
                    <span className="esign-status-sent-date">Sent {sentDate}</span>
                  ) : null}
                </div>
                <ul className="esign-status-signers">
                  {envelope.signers.map((signer) => {
                    // Both actions ask something of a person who hasn't
                    // answered yet. Once they have — signed or declined —
                    // there is nothing to chase and nowhere to move it.
                    const canAsk = pending && !hasFinished(signer);
                    const signerState = esignSignerStatus(signer.status);
                    const signedDate = formatDate(signer.signedAt ?? signer.declinedAt);
                    return (
                      <li key={signer.signerId} className="esign-status-signer-row">
                        <span className="esign-status-signer-who">
                          <span>
                            {signer.name} <span className="esign-status-email">{signer.email}</span>
                          </span>
                          <span className="esign-status-signer-state">
                            <StatusPill {...signerState} />
                            {signedDate ? (
                              <span className="esign-status-email">{signedDate}</span>
                            ) : null}
                          </span>
                          {/* Where this request started, when someone has
                              since moved it. Printed rather than kept in
                              the audit log alone, because "why is this
                              going to their gmail?" is asked on the record
                              months later, by whoever is looking at it. */}
                          {signer.previousEmail ? (
                            <span className="esign-status-email">
                              Was {signer.previousEmail}
                              {signer.emailChangedAt
                                ? ` — changed ${formatDate(signer.emailChangedAt)}`
                                : ""}
                            </span>
                          ) : null}
                        </span>
                        {canAsk ? (
                          <span className="esign-status-signer-actions">
                            <button
                              type="button"
                              className="esign-status-action"
                              disabled={busyKey === `resend:${envelope.id}:${signer.signerId}`}
                              onClick={() =>
                                handleResend(envelope.id, signer.signerId, signer.name)
                              }
                            >
                              Resend
                            </button>
                            <button
                              type="button"
                              className="esign-status-action"
                              onClick={() =>
                                setChangeTarget({
                                  envelopeId: envelope.id,
                                  signerId: signer.signerId,
                                  name: signer.name,
                                  email: signer.email,
                                })
                              }
                            >
                              Send elsewhere
                            </button>
                            {/* No email at all — for the advisor who
                                wants to text it themselves rather than
                                have the OS deliver it. */}
                            <button
                              type="button"
                              className="esign-status-action"
                              disabled={busyKey === `copy-link:${envelope.id}:${signer.signerId}`}
                              onClick={() =>
                                handleCopyLink(envelope.id, signer.signerId, signer.name)
                              }
                            >
                              Copy a link
                            </button>
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
                {/* No dead ends: the full journey — delivered, opened,
                    signed, with every timestamp and the whole delivery
                    history — is one click away for anyone who needs more
                    than the one word above. */}
                <Link to={`/esign/${envelope.id}`} className="esign-status-trail-link">
                  See the full signing trail
                </Link>
                {pending ? (
                  <button
                    type="button"
                    className="esign-status-action esign-status-action-cancel"
                    onClick={() => setCancelTarget(envelope)}
                  >
                    Cancel request
                  </button>
                ) : null}
                {/* The one thing this panel couldn't do before: get from
                    "it's signed" to the actual file. Filing is automatic
                    (esign-archive.ts) and always lands on this same
                    record's Documents tab — this is a shortcut there, not
                    a second copy. Only once the archive has actually run
                    (sealedDocumentId set) — a status flip to Completed can
                    briefly precede it. */}
                {envelope.sealedDocumentId ? (
                  <a
                    href={`/api/files/download/${encodeURIComponent(envelope.sealedDocumentId)}`}
                    className="esign-status-view-doc"
                  >
                    View signed document
                  </a>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>

      <ChangeSignerEmailDialog
        target={changeTarget}
        onClose={() => setChangeTarget(null)}
        onChanged={load}
        onFallbackLink={(link) =>
          setFallbackLink({
            ...link,
            hint: `A fresh link for ${link.name} — the old address's link is dead. Copy it and send it however you like.`,
          })
        }
      />

      <Modal
        open={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        title="Cancel this signature request?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCancelTarget(null)}>
              Never mind
            </Button>
            <Button
              variant="sw-danger"
              disabled={busyKey === `cancel:${cancelTarget?.id}`}
              onClick={() => cancelTarget && handleCancel(cancelTarget)}
            >
              Cancel request
            </Button>
          </>
        }
      >
        <p>
          "{cancelTarget?.title ?? "This document"}" will no longer be signable — every signer's
          link stops working right away, even if they still have the email. This can't be undone;
          you'd need to send it again from scratch.
        </p>
      </Modal>
    </>
  );
}

/** The packages section when nothing has ever been sent from the record.
 *  Still a named place (Nate, 2026-09-29): an empty section that says so
 *  is how the card teaches where a package will appear. */
/** "Daniel and Priya": who the void email goes to, approximating the
 *  server's pick (asked, and did not decline): a live link or a signature
 *  means they were asked. A later turn on an ordered package has neither. */
function voidNoticeNames(envelope: EsignEnvelopeRec | null): string {
  if (!envelope) return "";
  const names = envelope.signers
    .filter((s) => s.hasActiveLink || s.signedAt || s.status === "signed")
    .filter((s) => !(s.declinedAt || s.status === "declined"))
    .map((s) => s.name.trim().split(/\s+/)[0] ?? s.name);
  if (names.length === 0) return "";
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function InFlightEmpty() {
  return (
    <section className="doc-card-packages" aria-label="E-sign packages in flight">
      <h3 className="doc-card-sec">Out for signature · 0</h3>
      <p className="doc-card-empty">
        Nothing out for signature. <strong>+</strong> starts a package.
      </p>
    </section>
  );
}

/** "Bob and Sue" / "Bob, Sue and Ann". */
function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * The two cases, in plain words, before anything happens (mockup v3
 * screen 10). The provider keeps signatures already given when only the
 * unsigned part changes (PR 10b), and refuses to alter a document anyone
 * has signed, which is when the package is voided and sent again (PR 10a).
 */
function CorrectChoices({
  envelope,
  mode,
  onMode,
  fixAvailable,
}: {
  envelope: EsignEnvelopeRec;
  mode: "fix" | "change";
  onMode: (mode: "fix" | "change") => void;
  fixAvailable: boolean;
}) {
  const signers = envelope.signers;
  const signed = signers.filter((s) => s.signedAt || s.status === "signed").map((s) => s.name);
  const waiting = signers.filter((s) => !(s.signedAt || s.status === "signed")).map((s) => s.name);
  const choice = (value: "fix" | "change", disabled: boolean) => ({
    className: `doc-correct-choice${mode === value ? " doc-correct-choice--on" : ""}`,
    role: "radio" as const,
    "aria-checked": mode === value,
    "aria-disabled": disabled ? ("true" as const) : undefined,
    tabIndex: disabled ? -1 : 0,
    onClick: () => {
      if (!disabled) onMode(value);
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (!disabled && (e.key === " " || e.key === "Enter")) {
        e.preventDefault();
        onMode(value);
      }
    },
  });
  return (
    <div className="doc-correct" role="radiogroup" aria-label="What needs to change">
      <p className="doc-correct-doc">{envelope.title ?? "This package"}</p>
      <div {...choice("fix", !fixAvailable)}>
        <span className="doc-correct-rb" aria-hidden />
        <span>
          <span className="doc-correct-nm">Fix what hasn&apos;t been signed yet</span>
          <span className="doc-correct-mt">
            {signed.length > 0 ? `${joinNames(signed)}'s signature is kept. ` : ""}
            Add or move boxes for {waiting.length > 0 ? joinNames(waiting) : "the people still to sign"}, add a
            signer, or change the note. Nothing is voided, and anyone whose part changed gets a fresh link by email
            with your reason.
          </span>
        </span>
      </div>
      <div {...choice("change", false)}>
        <span className="doc-correct-rb" aria-hidden />
        <span>
          <span className="doc-correct-nm">Change the document itself</span>
          <span className="doc-correct-mt">
            The provider won&apos;t alter a document someone has signed. This voids the package (the signers are
            told, and the files stay on the record marked voided) and opens the same package again with its
            documents, signers, order, fields and email in place. You make the change and send it.
            {signed.length > 0 ? ` ${joinNames(signed)} ${signed.length === 1 ? "signs" : "sign"} again.` : ""}
          </span>
        </span>
      </div>
    </div>
  );
}
